import { useEffect, useMemo, useState } from "react";
import { api, fileSrc, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, CheckSquare, Square } from "lucide-react";
import { Btn, Field, Input, Area, Select, SectionTitle } from "./ui";

const key = (p) => p?.url || p?.path || "";

export function collectPhotos(ws) {
  const a = ws.appointment; const insp = ws.inspection || {};
  const out = []; const seen = new Set();
  const add = (list, group) => (list || []).forEach((p) => { const k = key(p); if (k && !seen.has(k)) { seen.add(k); out.push({ k, p, group }); } });
  add(insp.photos, "État des lieux");
  (insp.zone_defects || []).forEach((d) => { add(d.photos, "Défauts"); add(d.after_photos, "Après (défauts)"); });
  add(a.before_photos, "Avant"); add(a.after_photos, "Après");
  return out;
}

export function PhotoPicker({ photos, selected, setSelected }) {
  const all = photos.length > 0 && selected.length === photos.length;
  const toggle = (k) => setSelected(selected.includes(k) ? selected.filter((x) => x !== k) : [...selected, k]);
  if (!photos.length) return <p className="text-xs text-slate-400">Aucune photo disponible pour les rapports.</p>;
  return (
    <div className="space-y-2" data-testid="pdf-photo-picker">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="text-slate-500">Photos incluses : <b data-testid="pdf-photo-count">{selected.length}</b> / {photos.length}</span>
        <button type="button" data-testid="pdf-photo-all" onClick={() => setSelected(photos.map((x) => x.k))} className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-slate-600"><CheckSquare className="w-3.5 h-3.5" /> Tout sélectionner</button>
        <button type="button" data-testid="pdf-photo-none" onClick={() => setSelected([])} className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-slate-600"><Square className="w-3.5 h-3.5" /> Tout désélectionner</button>
        {all && <span className="text-emerald-700">Toutes les photos</span>}
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-7 gap-2">
        {photos.map(({ k, p, group }, i) => {
          const on = selected.includes(k);
          return (
            <button type="button" key={k} data-testid={`pdf-photo-${i}`} data-selected={on} onClick={() => toggle(k)}
              className={`relative rounded-lg overflow-hidden border-2 transition-colors ${on ? "border-emerald-500" : "border-transparent opacity-50"}`}>
              <img src={fileSrc(key(p))} alt="" className="w-full aspect-square object-cover" />
              <span className="absolute bottom-0 inset-x-0 bg-black/55 text-white text-[9px] px-1 py-0.5 truncate">{group}</span>
              <span className={`absolute top-1 right-1 w-4 h-4 rounded ${on ? "bg-emerald-500" : "bg-white/80 border border-slate-300"}`} />
            </button>
          );
        })}
      </div>
    </div>
  );
}

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "");
const ST = (r) => (r.status === "echec" || r.bounced ? ["Échec", "text-red-700 bg-red-50"] : r.opened ? ["Ouvert", "text-emerald-700 bg-emerald-50"] : r.delivered ? ["Délivré", "text-blue-700 bg-blue-50"] : ["Envoyé", "text-slate-700 bg-slate-100"]);

export function ReportEmail({ ws, query }) {
  const insp = ws.inspection;
  const [to, setTo] = useState(ws.client?.email || "");
  const [mode, setMode] = useState(insp?.after_at ? "full" : "arrival");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [hist, setHist] = useState([]);
  const load = useMemo(() => () => insp?.id && api.get(`/inspections/${insp.id}/email-history`).then((r) => setHist(r.data)).catch(() => {}), [insp?.id]);
  useEffect(() => { load(); }, [load]);
  if (!insp?.id) return null;
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/inspections/${insp.id}/send-email`, { mode, to_email: to || null, message: msg || null, ...query });
      toast.success(`Rapport envoyé à ${data.to}`); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); load(); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3 pt-3 border-t border-slate-100" data-testid="report-email">
      <SectionTitle>Envoyer le rapport par e-mail au client</SectionTitle>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Destinataire"><Input data-testid="report-email-to" type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="client@exemple.fr" /></Field>
        <Field label="Document"><Select data-testid="report-email-mode" value={mode} onChange={(e) => setMode(e.target.value)} options={[["arrival", "État des lieux à l'arrivée"], ["full", "Rapport complet avant / après"]]} /></Field>
        <Field label="Message (facultatif)" className="sm:col-span-2"><Area data-testid="report-email-msg" value={msg} onChange={(e) => setMsg(e.target.value)} /></Field>
      </div>
      <p className="text-[11px] text-slate-500">Les sections et photos sélectionnées ci-dessus sont appliquées au PDF joint.</p>
      <Btn data-testid="report-email-send" variant="dark" onClick={send} disabled={busy}><Mail className="w-4 h-4" /> {busy ? "Envoi…" : "Envoyer par e-mail"}</Btn>
      {!!hist.length && (
        <div className="space-y-1" data-testid="report-email-history">
          <div className="text-xs font-semibold text-slate-600">Suivi des envois</div>
          {hist.map((r) => { const [l, c] = ST(r); return (
            <div key={r.id} className="flex flex-wrap items-center gap-2 text-xs text-slate-600" data-testid="report-email-row">
              <span>{fmt(r.created_at)}</span><span className="truncate">{r.to}</span><span>{r.mode === "full" ? "Avant/après" : "Arrivée"}</span>
              <span className={`px-1.5 py-0.5 rounded ${c}`}>{l}</span>{r.error && <span className="text-red-600 truncate">{r.error}</span>}
            </div>); })}
        </div>
      )}
    </div>
  );
}
